/* @flow */
// react
import * as React from 'react';

// api
import { autoCompleteCatalogue } from 'api/forms';

// mobx
import { observer } from 'mobx-react';
import { reaction, action } from 'mobx';

// form components
import ElectroSelectExtra from 'forms/components/inputs/ElectroSelectExtra/ElectroSelectExtra';
import ElectroSimpleSelect from 'forms/components/inputs/ElectroSimpleSelect/ElectroSimpleSelect';

// models
import AutoCompleteData from 'models/forms/AutoCompleteModel';

// components
import SimpleSelect from 'components/SimpleSelect/SimpleSelect';
import Loading from 'components/Loading/Loading';
import { ExpandedList } from 'components/DocumentList/DocumentList';
import AddOrRemoveButtonControl from '../../components/controls/FormControls/AddOrRemoveButtonControl';

import DetailsSoftwareSection from './DetailsSoftwareSection';
import DetailsDisplaysOrPeripheralsSection from './DetailsDisplaysOrPeripheralsSection';

import CatalogueContext from '../utilities/CatalogueContext/CatalogueContext';

// styles
import styles from '../RushAssetRequestForm.module.scss';

type RushDetailProps = {
  form: Object,
  field: Object,
  index: number,
};

/** Creates a MobX reaction to watch over the `category` field changes,
 * when `No Selection` is chosen, it will delete all values from laptops
 * and tablets, which will no longer be displayed in the UI.
 */

function categoryFieldReaction(field: Object): () => void {
  return reaction(
    () => field.$('category').value,
    action('clearDependentFields', newValue => {
      if (newValue === 'No selection') {
        const fieldsToClear = [
          'quantity',
          'dataPlan',
          'software',
          'assetTitle',
          'description',
          'descriptionCategory',
        ];

        const hasValue = fieldsToClear.some(name => field.$(name).value);

        if (hasValue) {
          fieldsToClear.forEach(name => field.$(name).set('value', ''));
        }
      }
    })
  );
}

@observer
class RushAssetDetailSection extends React.Component<RushDetailProps> {
  disposer: ?() => void = null;

  componentDidMount() {
    const { field } = this.props;
    this.disposer = categoryFieldReaction(field);
  }

  componentWillUnmount() {
    if (this.disposer) {
      this.disposer();
    }
  }

  render() {
    const { form, field, index } = this.props;
    // $FlowFixMe[incompatible-call]
    const catalogueModel: AutoCompleteData = new AutoCompleteData(autoCompleteCatalogue);
    const fieldValues = field.values();

    return (
      <CatalogueContext.Consumer>
        {({ idsToExcludeForDescriptionSelect, idsToExcludeForDataPlanSelect, loading }) => (
          <ExpandedList
            isExpanded
            header={
              <>
                <span>
                  {`Asset ${index + 1} `}
                  {fieldValues.assetTitle && `(${fieldValues.assetTitle})`}
                </span>
                <AddOrRemoveButtonControl
                  form={form}
                  field={field}
                  controls={{
                    onDel: true,
                  }}
                />
              </>
            }
          >
            {loading ? (
              <div className={styles['loading-padding']}>
                <Loading />
              </div>
            ) : (
              <div className={styles['form-asset']}>
                <SimpleSelect
                  disabled={false}
                  label="Category"
                  value={field.$('category').value}
                  onChange={e => field.$('category').set('value', e.target.value)}
                  options={field.$('category').extra.options}
                />
                {field.$('category').value === 'Laptops & Tablets' && (
                  <>
                    <ElectroSelectExtra
                      field={field.$('description')}
                      form={form}
                      formName="rushAssetDetails"
                      labelKey="ui_displayed_description"
                      extraFilters={['aliases']}
                      props={{
                        model: catalogueModel,
                      }}
                      exclude={idsToExcludeForDescriptionSelect}
                    />
                    <ElectroSimpleSelect field={field.$('quantity')} />
                    {field.$('descriptionCategory').value === 'Tablets' && (
                      <ElectroSelectExtra
                        field={field.$('dataPlan')}
                        form={form}
                        formName="rushAssetDetails"
                        labelKey="ui_displayed_description"
                        extraFilters={['aliases']}
                        props={{
                          model: catalogueModel,
                        }}
                        exclude={idsToExcludeForDataPlanSelect}
                        clearOptionLabel="No selection"
                      />
                    )}
                    <DetailsSoftwareSection field={field.$('software')} />
                  </>
                )}
                <DetailsDisplaysOrPeripheralsSection
                  field={field.$('displays')}
                  assetTitleField={field.$('assetTitle')}
                  title="Displays"
                />
                <DetailsDisplaysOrPeripheralsSection
                  field={field.$('peripherals')}
                  assetTitleField={field.$('assetTitle')}
                  title="Peripherals"
                />
              </div>
            )}
          </ExpandedList>
        )}
      </CatalogueContext.Consumer>
    );
  }
}

export default RushAssetDetailSection;
